import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Plus, ArrowLeft, Trash, User, UsersThree, Kanban as KanbanIcon, ChartBar, Clock, Flag, ListChecks, Tag, Eye, CheckSquare } from "@phosphor-icons/react";
import TaskDetail from "@/components/TaskDetail";
import DynamicFields from "@/components/DynamicFields";
import ProjectLabelsModal, { LabelPicker } from "@/components/ProjectLabels";
import SprintCountdownBanner from "@/components/SprintCountdownBanner";
import { FilterPill, SavedViewsMenu } from "@/components/KanbanFilters";
import { ProjectMembersModal, ProjectLanesModal, ProjectTimesheetModal } from "@/components/ProjectSettings";

const DEFAULT_LANES = [
  { key: "todo", label: "To Do", accent: "#6B7280", order: 0 },
  { key: "in_progress", label: "In Progress", accent: "#002FA7", order: 1 },
  { key: "review", label: "Review", accent: "#F59E0B", order: 2 },
  { key: "done", label: "Done", accent: "#22C55E", order: 3 },
];

const PRIORITY_STYLES = {
  low: "bg-slate-100 text-slate-700 border-slate-300",
  medium: "bg-blue-50 text-blue-700 border-blue-300",
  high: "bg-orange-50 text-orange-700 border-orange-300",
  urgent: "bg-red-50 text-red-700 border-red-400",
};

export default function ProjectKanban() {
  const { id } = useParams();
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [activeSprint, setActiveSprint] = useState(null);
  const [multipleActive, setMultipleActive] = useState(false);
  const [sprintFilter, setSprintFilter] = useState(searchParams.get("sprint") || "");
  // Filter bar
  const [labelFilter, setLabelFilter] = useState([]); // array of label_ids
  const [assigneeFilter, setAssigneeFilter] = useState([]); // array of user_ids
  const [priorityFilter, setPriorityFilter] = useState([]); // low/medium/high/urgent
  const [typeFilter, setTypeFilter] = useState([]); // task/story/bug/epic
  const [hideDone, setHideDone] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [savedViews, setSavedViews] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creatingIn, setCreatingIn] = useState("todo");
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", task_type: "task", points: 0, assignee_id: "", due_date: "", custom_fields: {}, label_ids: [] });
  const [error, setError] = useState("");
  const [openTaskId, setOpenTaskId] = useState(searchParams.get("task") || null);
  const [showMembers, setShowMembers] = useState(false);
  const [showLanes, setShowLanes] = useState(false);
  const [showTsApprover, setShowTsApprover] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [projectLabels, setProjectLabels] = useState([]);
  const [templatesByType, setTemplatesByType] = useState({}); // for on-card snippets
  const [selected, setSelected] = useState(new Set()); // bulk selection: task ids
  const [bulkBusy, setBulkBusy] = useState(false);
  const canManage = user.role !== "employee";

  const load = async () => {
    try {
      const [p, t, sp] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/tasks`),
        api.get(`/projects/${id}/sprints`).catch(() => ({ data: { sprints: [], active_sprint: null } })),
      ]);
      setProject(p.data);
      setTasks(t.data);
      setSprints(sp.data.sprints || []);
      setActiveSprint(sp.data.active_sprint || null);
      setMultipleActive(!!sp.data.multiple_active_warning);
      // If no explicit sprint filter chosen yet, auto-select the active sprint
      // when there is one. Only fire once per project load — after that we
      // respect whatever the user picked.
      setSprintFilter(prev => {
        if (prev && prev !== "") return prev;
        if (sp.data.active_sprint) return "active";
        return "all";
      });
      if (canManage) {
        try {
          const u = await api.get("/users");
          setUsers(u.data);
        } catch {
          // non-fatal — user list is optional
        }
      } else {
        // Employees also need the user list so the task detail drawer can
        // render the assignee name / watcher chips / mention popup. `/users`
        // now returns a minimal, RBAC-safe projection for non-privileged
        // callers.
        try {
          const u = await api.get("/users");
          setUsers(u.data);
        } catch {
          // non-fatal — falls back to id-only rendering
        }
      }
    } catch (err) {
      console.warn("Failed to load project:", err?.response?.status || err?.message);
    }
  };
  useEffect(() => { load(); }, [id]);

  // Load task-form templates (effective = global + this project's extras) so we
  // can render on-card snippets for any custom field with `appears_on_card: true`.
  useEffect(() => {
    if (!id) return;
    (async () => {
      const map = {};
      for (const t of ["task", "story", "bug", "epic"]) {
        try {
          const r = await api.get(`/settings/task-forms/effective/${t}`, { params: { project_id: id } });
          map[t] = r.data.fields || [];
        } catch { map[t] = []; }
      }
      setTemplatesByType(map);
    })();
  }, [id]);

  const loadLabels = () => {
    api.get(`/projects/${id}/labels`)
      .then(r => setProjectLabels(r.data || []))
      .catch(() => setProjectLabels([]));
  };
  useEffect(() => { if (id) loadLabels(); }, [id]);
  const labelsById = Object.fromEntries((projectLabels || []).map(lb => [lb.id, lb]));

  // Saved views for this project (personal + shared)
  const loadSavedViews = () => {
    api.get(`/projects/${id}/kanban-views`)
      .then(r => setSavedViews(r.data || []))
      .catch(() => setSavedViews([]));
  };
  useEffect(() => { if (id) loadSavedViews(); }, [id]);

  const currentFilters = () => ({
    sprintFilter,
    labelFilter,
    assigneeFilter,
    priorityFilter,
    typeFilter,
    hideDone,
    searchQ,
  });

  const applyView = (f = {}) => {
    if ("sprintFilter" in f) setSprintFilter(f.sprintFilter || "all");
    setLabelFilter(f.labelFilter || []);
    setAssigneeFilter(f.assigneeFilter || []);
    setPriorityFilter(f.priorityFilter || []);
    setTypeFilter(f.typeFilter || []);
    setHideDone(!!f.hideDone);
    setSearchQ(f.searchQ || "");
  };

  const saveCurrentView = async ({ name, shared }) => {
    try {
      await api.post(`/projects/${id}/kanban-views`, { name, shared, filters: currentFilters() });
      loadSavedViews();
    } catch (err) { console.warn(err); }
  };
  const deleteView = async (v) => {
    const ok = await confirm({
      title: `Delete view "${v.name}"?`,
      description: "The saved filter view will be removed. Other members using this view will lose access.",
      confirmText: "Delete view",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/projects/${id}/kanban-views/${v.id}`);
      loadSavedViews();
    } catch (err) { console.warn(err); }
  };

  const lanes = (project?.lanes && project.lanes.length ? project.lanes : DEFAULT_LANES)
    .slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination, source } = result;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    const newStatus = destination.droppableId;
    setTasks(prev => prev.map(t => t.id === draggableId ? { ...t, status: newStatus } : t));
    try {
      await api.patch(`/tasks/${draggableId}`, { status: newStatus, order: destination.index });
    } catch (e) {
      load();
    }
  };

  const openCreate = (status) => {
    setCreatingIn(status);
    setForm({ title: "", description: "", priority: "medium", task_type: "task", points: 0, assignee_id: "", due_date: "", custom_fields: {}, label_ids: [] });
    setShowCreate(true);
  };

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/tasks", {
        project_id: id,
        title: form.title,
        description: form.description,
        status: creatingIn,
        priority: form.priority,
        task_type: form.task_type,
        points: Number(form.points) || 0,
        assignee_id: form.assignee_id || null,
        due_date: form.due_date || null,
        custom_fields: form.custom_fields || {},
        label_ids: form.label_ids || [],
      });
      setShowCreate(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ============ BULK OPS ============
  const toggleSelect = (tid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      return next;
    });
  };
  const clearSelected = () => setSelected(new Set());
  const bulk = async (updates) => {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      await api.post("/tasks/bulk-update", { task_ids: [...selected], updates });
      clearSelected();
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBulkBusy(false); }
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    const ok = await confirm({
      title: `Delete ${selected.size} task${selected.size === 1 ? "" : "s"}?`,
      description: "This cannot be undone. Selected tasks and their attachments will be permanently removed.",
      confirmText: "Delete tasks",
      variant: "destructive",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      await api.post("/tasks/bulk-delete", { task_ids: [...selected] });
      clearSelected();
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBulkBusy(false); }
  };

  const deleteTask = async (tid) => {
    const ok = await confirm({
      title: "Delete this task?",
      description: "This will permanently delete the task and its comments.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    await api.delete(`/tasks/${tid}`);
    load();
  };

  const openTask = (tid) => {
    setOpenTaskId(tid);
    setSearchParams({ task: tid });
  };
  const closeTask = () => {
    setOpenTaskId(null);
    searchParams.delete("task");
    setSearchParams(searchParams);
  };

  const usersById = Object.fromEntries(users.map(u => [u.id, u]));

  if (!project) return <div className="p-12 text-sm text-muted-foreground">Loading…</div>;

  const filteredTasks = tasks.filter(t => {
    // Sprint filter
    if (sprintFilter === "backlog" && t.sprint_id) return false;
    if (sprintFilter === "active" && !(activeSprint && t.sprint_id === activeSprint.id)) return false;
    if (sprintFilter && sprintFilter !== "all" && sprintFilter !== "backlog" && sprintFilter !== "active" && t.sprint_id !== sprintFilter) return false;
    // Label filter (must include ANY selected label)
    if (labelFilter.length && !(t.label_ids || []).some(x => labelFilter.includes(x))) return false;
    // Assignee filter
    if (assigneeFilter.length) {
      const aid = t.assignee_id || "__unassigned__";
      if (!assigneeFilter.includes(aid)) return false;
    }
    if (priorityFilter.length && !priorityFilter.includes(t.priority)) return false;
    if (typeFilter.length && !typeFilter.includes(t.task_type)) return false;
    if (hideDone && t.status === "done") return false;
    if (searchQ.trim()) {
      const q = searchQ.trim().toLowerCase();
      if (!(t.title || "").toLowerCase().includes(q) && !(t.description || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const anyFilterOn = labelFilter.length || assigneeFilter.length || priorityFilter.length || typeFilter.length || hideDone || searchQ.trim();
  const clearFilters = () => { setLabelFilter([]); setAssigneeFilter([]); setPriorityFilter([]); setTypeFilter([]); setHideDone(false); setSearchQ(""); };
  const tasksByStatus = (s) => filteredTasks.filter(t => t.status === s);

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-6">
        <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#002FA7]" data-testid="back-to-projects">
          <ArrowLeft size={14} weight="bold" /> Back to projects
        </Link>
        <div className="flex items-end justify-between mt-3 gap-4 flex-wrap">
          <div>
            <div className="eyebrow mb-1">Board</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="project-title">{project.name}</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{project.description || "No description."}</p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {(project.member_details || []).slice(0, 6).map(m => (
                <div key={m.id} title={m.name} className="w-7 h-7 -ml-1 border-2 border-white bg-[#0A0A0A] text-white text-[10px] flex items-center justify-center font-bold">
                  {m.name?.[0]?.toUpperCase() || "?"}
                </div>
              ))}
              {(project.member_details || []).length > 6 && (
                <div className="text-xs text-muted-foreground font-mono-mini">+{project.member_details.length - 6}</div>
              )}
              <div className="text-xs text-muted-foreground font-mono-mini ml-2">
                {(project.members || []).length} members
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canManage && (
              <>
                <button onClick={() => setShowMembers(true)} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="btn-members">
                  <UsersThree size={14} weight="bold" /> Members
                </button>
                <button onClick={() => setShowLanes(true)} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="btn-lanes">
                  <KanbanIcon size={14} weight="bold" /> Lanes
                </button>
                <button onClick={() => setShowLabels(true)} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="btn-labels">
                  <Tag size={14} weight="bold" /> Labels
                </button>
                <button onClick={() => setShowTsApprover(true)} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="btn-ts-approver">
                  <Clock size={14} weight="bold" /> Approver
                </button>
                <Link to={`/projects/${id}/report`} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="btn-report">
                  <ChartBar size={14} weight="bold" /> Report
                </Link>
                <Link to={`/projects/${id}/sprints`} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="btn-sprints">
                  <Flag size={14} weight="bold" /> Sprints
                </Link>
                <Link to={`/projects/${id}/backlog`} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="btn-backlog">
                  <ListChecks size={14} weight="bold" /> Backlog
                </Link>
                <Link to={`/projects/${id}/budget`} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="btn-budget">
                  💰 Budget
                </Link>
                <Link to={`/projects/${id}/files`} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="btn-files">
                  📁 Files
                </Link>
              </>
            )}
            <div className="text-right ml-2">
              <div className="eyebrow">Status</div>
              <div className="font-mono-mini uppercase text-sm">{project.status}</div>
              {project.end_date && <div className="text-xs text-muted-foreground mt-1">Due {project.end_date}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Sprint countdown banner + multi-active warning */}
      <SprintCountdownBanner sprint={activeSprint} projectId={id} multipleActive={multipleActive} />

      <div className="px-8 lg:px-12 py-3 border-b border-border flex items-center gap-3 flex-wrap bg-muted/30">
        <div className="eyebrow text-[10px]">Sprint filter</div>
        <select
          value={sprintFilter}
          onChange={(e) => setSprintFilter(e.target.value)}
          className="border border-border px-3 py-1.5 text-sm bg-white"
          data-testid="sprint-filter"
        >
          <option value="all">All tasks</option>
          {activeSprint && <option value="active">Active sprint · {activeSprint.name}</option>}
          <option value="backlog">Backlog only</option>
          {sprints.filter(s => s.status !== "cancelled").map(s => (
            <option key={s.id} value={s.id}>{s.name} ({s.status})</option>
          ))}
        </select>
        <input
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Search title / description…"
          className="border border-border px-3 py-1.5 text-sm min-w-[220px]"
          data-testid="search-filter"
        />
        <FilterPill
          label="Label"
          testId="filter-label"
          selected={labelFilter}
          options={projectLabels.map(lb => ({ value: lb.id, label: lb.name, dot: lb.color }))}
          onChange={setLabelFilter}
        />
        <FilterPill
          label="Assignee"
          testId="filter-assignee"
          selected={assigneeFilter}
          options={[
            { value: "__unassigned__", label: "Unassigned" },
            ...(users || []).filter(u => project?.members?.includes(u.id)).map(u => ({ value: u.id, label: u.name })),
          ]}
          onChange={setAssigneeFilter}
        />
        <FilterPill
          label="Priority"
          testId="filter-priority"
          selected={priorityFilter}
          options={[{value:"low",label:"Low"},{value:"medium",label:"Medium"},{value:"high",label:"High"},{value:"urgent",label:"Urgent"}]}
          onChange={setPriorityFilter}
        />
        <FilterPill
          label="Type"
          testId="filter-type"
          selected={typeFilter}
          options={[{value:"task",label:"Task"},{value:"story",label:"Story"},{value:"bug",label:"Bug"},{value:"epic",label:"Epic"}]}
          onChange={setTypeFilter}
        />
        <label className="inline-flex items-center gap-1 text-xs">
          <input type="checkbox" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} data-testid="filter-hide-done" />
          Hide done
        </label>
        {anyFilterOn && (
          <button onClick={clearFilters} className="text-xs underline text-[#002FA7]" data-testid="filter-clear">Clear</button>
        )}
        <SavedViewsMenu
          projectId={id}
          userRole={user.role}
          views={savedViews}
          onLoad={(v) => applyView(v.filters)}
          onSave={saveCurrentView}
          onDelete={deleteView}
          canShareViews={user.role === "admin" || user.role === "manager"}
        />
        <div className="ml-auto text-xs text-muted-foreground font-mono-mini">
          {filteredTasks.length} of {tasks.length} tasks visible
        </div>
      </div>

      <div className="p-6 lg:p-8 overflow-x-auto">
        {/* Bulk actions bar */}
        {canManage && selected.size > 0 && (
          <div className="sticky top-0 z-20 mb-4 -mt-2 bg-[#002FA7] text-white border border-[#002FA7] px-4 py-2 flex items-center gap-3 flex-wrap shadow-md" data-testid="bulk-bar">
            <div className="text-sm font-semibold">{selected.size} selected</div>
            <select
              onChange={(e) => { if (e.target.value) { bulk({ status: e.target.value }); e.target.value = ""; } }}
              disabled={bulkBusy}
              className="text-xs bg-white text-black px-2 py-1"
              data-testid="bulk-status"
              defaultValue=""
            >
              <option value="" disabled>Change status…</option>
              {(project?.lanes || []).map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
            <select
              onChange={(e) => { if (e.target.value) { bulk({ priority: e.target.value }); e.target.value = ""; } }}
              disabled={bulkBusy}
              className="text-xs bg-white text-black px-2 py-1"
              data-testid="bulk-priority"
              defaultValue=""
            >
              <option value="" disabled>Change priority…</option>
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
            </select>
            {users.length > 0 && (
              <select
                onChange={(e) => { if (e.target.value !== "") { bulk({ assignee_id: e.target.value || null }); e.target.value = ""; } }}
                disabled={bulkBusy}
                className="text-xs bg-white text-black px-2 py-1"
                data-testid="bulk-assignee"
                defaultValue=""
              >
                <option value="" disabled>Assign to…</option>
                <option value="__unassign__" disabled>—</option>
                {users.filter(u => project?.members?.includes(u.id)).map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            )}
            <button onClick={bulkDelete} disabled={bulkBusy} className="text-xs border border-white/50 px-2 py-1 hover:bg-white/10 flex items-center gap-1" data-testid="bulk-delete">
              <Trash size={12} weight="bold" /> Delete
            </button>
            <button onClick={clearSelected} className="ml-auto text-xs text-white/80 hover:text-white underline">Clear</button>
          </div>
        )}
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-4 min-w-max" data-testid="kanban-board">
            {lanes.map((lane) => {
              const laneTasks = tasksByStatus(lane.key);
              return (
                <div key={lane.key} className="kanban-column p-3 w-72 shrink-0" data-testid={`lane-${lane.key}`}>
                  <div className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2" style={{ background: lane.accent }} />
                      <div className="eyebrow" style={{ color: lane.accent }}>{lane.label}</div>
                      <span className="text-xs text-muted-foreground font-mono-mini">{laneTasks.length}</span>
                    </div>
                    <button onClick={() => openCreate(lane.key)} className="p-1 hover:bg-white" data-testid={`add-task-${lane.key}`}>
                      <Plus size={14} weight="bold" />
                    </button>
                  </div>
                  <Droppable droppableId={lane.key}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-2 min-h-[400px] transition-colors ${snapshot.isDraggingOver ? "bg-white/50" : ""}`}
                      >
                        {laneTasks.map((task, index) => (
                          <Draggable key={task.id} draggableId={task.id} index={index}>
                            {(prov, snap) => (
                              <div
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                                onClick={() => openTask(task.id)}
                                className={`kanban-card p-3 cursor-pointer ${snap.isDragging ? "dragging" : ""} ${selected.has(task.id) ? "ring-2 ring-[#002FA7]" : ""}`}
                                data-testid={`task-${task.id}`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  {canManage && (
                                    <input
                                      type="checkbox"
                                      checked={selected.has(task.id)}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={() => toggleSelect(task.id)}
                                      className="mt-0.5"
                                      data-testid={`task-select-${task.id}`}
                                    />
                                  )}
                                  <div className="text-sm font-semibold leading-snug flex-1">{task.title}</div>
                                  {canManage && (
                                    <button onClick={(e) => { e.stopPropagation(); deleteTask(task.id); }} className="text-muted-foreground hover:text-[#FF3B30] shrink-0" data-testid={`delete-task-${task.id}`}>
                                      <Trash size={14} />
                                    </button>
                                  )}
                                </div>
                                {task.description && (
                                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.description}</div>
                                )}
                                {/* Label pills */}
                                {Array.isArray(task.label_ids) && task.label_ids.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1" data-testid={`task-labels-${task.id}`}>
                                    {task.label_ids.map(lid => {
                                      const lb = labelsById[lid];
                                      if (!lb) return null;
                                      return (
                                        <span key={lid} className="inline-flex items-center gap-1 text-[10px] font-mono-mini px-1.5 py-0.5 border" style={{ borderColor: lb.color, color: lb.color, backgroundColor: `${lb.color}14` }}>
                                          <span className="inline-block w-1.5 h-1.5" style={{ backgroundColor: lb.color }} />
                                          {lb.name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                  {task.task_type && (
                                    <span
                                      className={`text-[10px] uppercase font-mono-mini border px-1.5 py-0.5 ${
                                        task.task_type === "bug"
                                          ? "border-[#DC2626] text-[#DC2626] bg-[#FEF2F2]"
                                          : task.task_type === "story"
                                          ? "border-[#059669] text-[#059669] bg-[#ECFDF5]"
                                          : task.task_type === "epic"
                                          ? "border-[#7C3AED] text-[#7C3AED] bg-[#F5F3FF]"
                                          : "border-[#0A0A0A] text-[#0A0A0A] bg-[#F5F5F5]"
                                      }`}
                                      data-testid={`task-type-${task.id}`}
                                    >
                                      {task.task_type}
                                    </span>
                                  )}
                                  {task.points > 0 && (
                                    <span className="text-[10px] font-mono-mini border border-[#0A0A0A] bg-[#0A0A0A] text-white px-1.5 py-0.5" data-testid={`task-points-${task.id}`}>
                                      {task.points} pt
                                    </span>
                                  )}
                                  <span className={`text-[10px] uppercase font-mono-mini border px-1.5 py-0.5 ${PRIORITY_STYLES[task.priority]}`}>
                                    {task.priority}
                                  </span>
                                  {task.due_date && (
                                    <span className="text-[10px] font-mono-mini text-muted-foreground">{task.due_date}</span>
                                  )}
                                  {(() => {
                                    const subs = Array.isArray(task.subtasks) ? task.subtasks : [];
                                    if (!subs.length) return null;
                                    const done = subs.filter(s => s.done).length;
                                    return (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-mono-mini text-muted-foreground" title={`${done}/${subs.length} subtasks done`} data-testid={`task-subtasks-${task.id}`}>
                                        <CheckSquare size={10} weight="bold" />
                                        {done}/{subs.length}
                                      </span>
                                    );
                                  })()}
                                  {Array.isArray(task.watchers) && task.watchers.length > 0 && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-mono-mini text-muted-foreground" title={`${task.watchers.length} watcher(s)`} data-testid={`task-watchers-${task.id}`}>
                                      <Eye size={10} weight="bold" /> {task.watchers.length}
                                    </span>
                                  )}
                                  {task.assignee_id && usersById[task.assignee_id] && (
                                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                                      <User size={10} weight="bold" />
                                      {usersById[task.assignee_id].name.split(" ")[0]}
                                    </span>
                                  )}
                                </div>
                                {/* Custom field snippets (appears_on_card fields) */}
                                {(() => {
                                  const cf = task.custom_fields || {};
                                  const fields = (templatesByType[task.task_type] || []).filter(f => f.appears_on_card && cf[f.key] !== undefined && cf[f.key] !== null && cf[f.key] !== "");
                                  if (!fields.length) return null;
                                  return (
                                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`task-cf-${task.id}`}>
                                      {fields.map(f => {
                                        const v = cf[f.key];
                                        const disp = Array.isArray(v) ? v.join(", ") : (typeof v === "boolean" ? (v ? "yes" : "no") : String(v));
                                        return (
                                          <span key={f.key} className="text-[10px] font-mono-mini border border-border bg-white px-1.5 py-0.5" title={`${f.label}: ${disp}`}>
                                            <span className="text-muted-foreground">{f.label}:</span> <b className="text-foreground">{disp}</b>
                                          </span>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white border border-border w-full max-w-md p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-task-dialog">
            <div className="eyebrow mb-2">In "{lanes.find(l => l.key === creatingIn)?.label}"</div>
            <h2 className="font-display text-2xl font-bold mb-6">New task</h2>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="eyebrow block mb-2">Title</label>
                <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="task-title-input" />
              </div>
              <div>
                <label className="eyebrow block mb-2">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="task-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">Type</label>
                  <select value={form.task_type} onChange={(e) => setForm({...form, task_type: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white" data-testid="task-type-select">
                    <option value="task">Task</option><option value="story">Story</option><option value="bug">Bug</option><option value="epic">Epic</option>
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Points</label>
                  <input type="number" min="0" value={form.points} onChange={(e) => setForm({...form, points: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="task-points-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">Priority</label>
                  <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="task-priority-select">
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Due date</label>
                  <input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="task-due-date-input" />
                </div>
              </div>
              {users.length > 0 && (
                <div>
                  <label className="eyebrow block mb-2">Assignee</label>
                  <select value={form.assignee_id} onChange={(e) => setForm({...form, assignee_id: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="task-assignee-select">
                    <option value="">Unassigned</option>
                    {users.filter(u => project.members?.includes(u.id) || u.id === user.id).map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <DynamicFields
                taskType={form.task_type}
                value={form.custom_fields}
                onChange={(v) => setForm({ ...form, custom_fields: v })}
                users={users}
                projectId={id}
              />
              <LabelPicker
                projectId={id}
                value={form.label_ids}
                onChange={(v) => setForm({ ...form, label_ids: v })}
              />
              {error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
                <button type="submit" className="flex-1 btn-primary" data-testid="submit-task">Create task</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {openTaskId && (
        <TaskDetail
          taskId={openTaskId}
          project={project}
          users={users}
          onClose={closeTask}
          onChanged={load}
        />
      )}

      {showMembers && (
        <ProjectMembersModal project={project} onClose={() => setShowMembers(false)} onChanged={load} />
      )}
      {showLabels && (
        <ProjectLabelsModal projectId={id} canManage={canManage} onClose={() => setShowLabels(false)} onChanged={() => { loadLabels(); load(); }} />
      )}

      {showLanes && (
        <ProjectLanesModal project={project} onClose={() => setShowLanes(false)} onSaved={() => load()} />
      )}
      {showTsApprover && (
        <ProjectTimesheetModal project={project} onClose={() => setShowTsApprover(false)} onSaved={() => load()} />
      )}
    </div>
  );
}
